import React from "react";
import { Trash2 } from "lucide-react";
import { API_BASE_URL } from '../services/api';

const StoreUpdateForm = ({ item, brandList, setBrandList, onCancel, onSave }) => {

    const isLitre = item?.indent?.toLowerCase() === "litre";
    const isNos = (() => {
        const k = item?.indent?.toLowerCase();
        return k === "nos" || k === "packets";
    })();

    const [rateTrend, setRateTrend] = React.useState(null);

    React.useEffect(() => {
        const fetchTrend = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const res = await fetch(`${API_BASE_URL}/item/rate-trend/${item.id}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                const result = await res.json();
                if (!result.error && Array.isArray(result.data)) setRateTrend(result.data);
            } catch (e) { console.error('rate-trend fetch failed', e); }
        };
        if (item?.id) fetchTrend();
    }, [item.id]);

    const updateBrand = (brandIndex, packIndex, field, value) => {
        const updated = [...brandList];
        updated[brandIndex].packs[packIndex][field] = value;

        const pack = updated[brandIndex].packs[packIndex];
        let qtyUnit = 0;

        if (isNos) {
            qtyUnit = 1;
        } else if (!isNaN(Number(pack.packQty)) && pack.packQty !== "others" && pack.packQty !== "loose") {
            qtyUnit = Number(pack.packQty);
        } else if (pack.packQty === "others") {
            const q = Number(pack.otherQty || 0);
            if (isLitre) {
                if (pack.otherUnit === "ml") qtyUnit = q / 1000;
                else qtyUnit = q;
            } else {
                if (pack.otherUnit === "Kg") qtyUnit = q;
                else if (pack.otherUnit === "Grams") qtyUnit = q / 1000;
                else if (pack.otherUnit === "Pcs") qtyUnit = q;
            }
        } else if (pack.packQty === "loose") {
            qtyUnit = 1;
        }

        if (pack.rate && qtyUnit > 0) {
            pack.autoRate = (Number(pack.rate) / qtyUnit).toFixed(2);
        } else {
            pack.autoRate = "";
        }

        setBrandList(updated);
    };

    const addPackRow = (brandIndex) => {
        const updated = [...brandList];
        updated[brandIndex].packs.push({ packQty: "", rate: "", autoRate: "" });
        setBrandList(updated);
    };

    const removePackRow = (brandIndex, packIndex) => {
        const updated = [...brandList];
        updated[brandIndex].packs = updated[brandIndex].packs.filter((_, i) => i !== packIndex);
        setBrandList(updated);
    };

    const addBrandRow = () => {
        setBrandList([
            ...brandList,
            { brandType: "", brand: "", packs: [{ packQty: "", rate: "", autoRate: "" }] }
        ]);
    };

    const removeBrandRow = (index) => {
        setBrandList(brandList.filter((_, i) => i !== index));
    };

    const getMinRateAndCheck = () => {
        let minRate = Infinity;
        let hasHighRate = false;
        brandList.forEach(brand => {
            brand.packs.forEach(pack => {
                if (pack.autoRate && pack.autoRate !== "") {
                    const rate = Number(pack.autoRate);
                    if (rate < minRate) minRate = rate;
                }
            });
        });
        if (minRate !== Infinity) {
            brandList.forEach(brand => {
                brand.packs.forEach(pack => {
                    if (pack.autoRate && pack.autoRate !== "") {
                        if (Number(pack.autoRate) > minRate * 1.1) hasHighRate = true;
                    }
                });
            });
        }
        return { minRate: minRate === Infinity ? 0 : minRate, hasHighRate };
    };

    const { hasHighRate } = getMinRateAndCheck();

    const rateUnitLabel = isLitre ? "Rate / Ltr" : isNos ? "Rate / Pc" : "Rate / Kg";

    const looseAlertLabel = isLitre
        ? "Enter Rate as Rate per Litre"
        : isNos
        ? "Enter Rate as Rate per Pc"
        : "Enter Rate as Rate per Kg";

    const getPackOptions = (brandType) => {
        if (isNos) return [{ value: "1", label: "1 Pc" }];
        if (isLitre) return [
            { value: "0.1", label: "100 ml" },
            { value: "0.2", label: "200 ml" },
            { value: "0.25", label: "250 ml" },
            { value: "0.5", label: "500 ml" },
            { value: "1", label: "1 Litre" },
            { value: "2", label: "2 Litre" },
            { value: "others", label: "Others" },
            { value: "loose", label: "Loose Qty" },
        ];
        return [
            { value: "0.1", label: "100 Gm" },
            { value: "0.2", label: "200 Gm" },
            { value: "0.25", label: "250 Gm" },
            { value: "0.5", label: "500 Gm" },
            { value: "1", label: "1 Kg" },
            { value: "2", label: "2 Kg" },
            { value: "others", label: "Others" },
            { value: "loose", label: "Loose Qty" },
        ];
    };

    const showGST = (p) => true;

    const GST_OPTIONS = [0, 5, 12, 18];

    const getPackGst = (p) => {
        if (p.gst !== undefined && p.gst !== null && p.gst !== "") return Number(p.gst);
        const itemGst = Number(item.gst);
        return isNaN(itemGst) ? 0 : itemGst;
    };

    const getPackGstType = (p) => p.gstType || "inclusive";

    const computePostGstRate = (p) => {
        const base = Number(p.autoRate);
        if (!base || isNaN(base)) return null;
        const gst = getPackGst(p);
        if (getPackGstType(p) === "inclusive") return base;
        return base * (1 + gst / 100);
    };

    const computeGstAmount = (p) => {
        const base = Number(p.autoRate);
        if (!base || isNaN(base)) return null;
        const gst = getPackGst(p);
        if (gst === 0) return 0;
        if (getPackGstType(p) === "inclusive") {
            return base - base / (1 + gst / 100);
        }
        return base * (gst / 100);
    };

    const [showSuppliers, setShowSuppliers] = React.useState(null); // 'district' | 'state' | null
    const [saveError, setSaveError] = React.useState(null); // { title, lines: [] }

    const BRAND_TYPE_ORDER = ['Cooperative', 'JPC', 'Non-JPC'];

    const buildTrendRows = () => {
        if (!rateTrend || rateTrend.length === 0) return { brandTypes: [], districtData: {}, stateData: {}, districtName: 'District', jpcRate: null, stateJpcRate: null, suppliers: [], stateSuppliers: [] };

        // New API: entries have a "type" field — "rate-trend" or "jpc-rate"
        const rateTrendRows = rateTrend.filter(r => r.type === 'rate-trend');
        const jpcRateRows   = rateTrend.filter(r => r.type === 'jpc-rate');

        const stateRow    = rateTrendRows.find(r => r.name.toLowerCase() === 'state') || null;
        const districtRow = rateTrendRows.find(r => r.name.toLowerCase() !== 'state') || null;

        const stateJpcRow    = jpcRateRows.find(r => r.name.toLowerCase() === 'state') || null;
        const districtJpcRow = jpcRateRows.find(r => r.name.toLowerCase() !== 'state') || null;

        const rawJpc = districtJpcRow?.rate;
        const jpcRate = rawJpc && typeof rawJpc === 'object' ? rawJpc.rate ?? null : (rawJpc ?? null);
        const suppliers = rawJpc && typeof rawJpc === 'object' ? rawJpc.suppliers || [] : [];

        const rawStateJpc = stateJpcRow?.rate ?? null;
        const stateJpcRate = rawStateJpc && typeof rawStateJpc === 'object' ? rawStateJpc.rate ?? null : rawStateJpc;
        const stateSuppliers = rawStateJpc && typeof rawStateJpc === 'object' ? rawStateJpc.suppliers || [] : [];

        const allTypes = new Set([
            ...Object.keys(districtRow?.rate || {}),
            ...Object.keys(stateRow?.rate || {}),
        ]);
        const sortedTypes = BRAND_TYPE_ORDER.filter(t => allTypes.has(t));

        return {
            brandTypes: sortedTypes,
            districtData: districtRow?.rate || {},
            stateData: stateRow?.rate || {},
            districtName: districtRow?.name || 'District',
            jpcRate,
            stateJpcRate,
            suppliers,
            stateSuppliers,
        };
    };

    const { brandTypes, districtData, stateData, districtName, jpcRate, stateJpcRate, suppliers, stateSuppliers } = buildTrendRows();

    return (
        <div className="w-full bg-white p-6 border border-gray-300 rounded-xl shadow relative">

            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">{item.name}</h3>
                <button
                    onClick={onCancel}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-400 text-gray-700 hover:bg-red-500 hover:text-white transition"
                >✕</button>
            </div>

            {brandList.map((brand, bi) => (
                <div key={bi} className="border rounded-lg p-4 mb-4 bg-gray-50">

                    {/* BRAND TYPE + NAME ROW */}
                    <div className="mb-4 flex gap-3 items-end">
                        <div className="w-48">
                            <label className="text-gray-700 font-medium block mb-1">Brand Type</label>
                            <select
                                className="border rounded px-3 py-2 w-full"
                                value={brand.brandType || ""}
                                onChange={(e) => {
                                    const newType = e.target.value;
                                    const updated = [...brandList];
                                    updated[bi].brandType = newType;
                                    if (newType === 'Non-JPC') {
                                        updated[bi].packs = updated[bi].packs.map(pk => {
                                            const currentGst = pk.gst !== undefined && pk.gst !== null && pk.gst !== ''
                                                ? Number(pk.gst)
                                                : Number(item.gst) || 0;
                                            if (currentGst === 0) return { ...pk, gst: 5 };
                                            return pk;
                                        });
                                    }
                                    setBrandList(updated);
                                }}
                            >
                                <option value="">Select</option>
                                <option value="Cooperative">Cooperative</option>
                                <option value="JPC">JPC / Non-JPC</option>
                                <option value="Non-JPC">Branded</option>
                            </select>
                        </div>

                        <div className="flex-1">
                            <label className="text-gray-700 font-medium block mb-1">
                                Brand Name
                                {brand.brandType === 'Non-JPC' && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <input
                                type="text"
                                className={`border rounded px-3 py-2 w-full ${
                                    (brand.brandType === 'Non-JPC' && !brand.brand.trim()) ||
                                    brandList.filter((b, idx) =>
                                        idx !== bi &&
                                        b.brand.trim().toLowerCase() === brand.brand.trim().toLowerCase() &&
                                        b.brand.trim() !== ''
                                    ).length > 0 ? 'border-red-500 bg-red-50' : ''
                                }`}
                                value={brand.brand}
                                onChange={(e) => {
                                    const updated = [...brandList];
                                    updated[bi].brand = e.target.value;
                                    setBrandList(updated);
                                }}
                            />
                            {brand.brandType === 'Non-JPC' && !brand.brand.trim() && (
                                <p className="text-red-500 text-xs mt-1">Brand name is required for Branded</p>
                            )}
                        </div>

                        {brandList.length > 1 && (
                            <button onClick={() => removeBrandRow(bi)} className="text-red-500 hover:text-red-700 p-2" title="Delete Brand">
                                <Trash2 className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    {/* PACK ROWS */}
                    {brand.packs.map((p, pi) => (
                        <div key={pi} className="border rounded p-3 mb-3 bg-white">

                            {isNos ? (
                                <>
                                    <div className="grid gap-3 mb-2 items-end grid-cols-4">
                                        <div className="col-span-2">
                                            <label className="text-gray-700 font-medium block mb-1">1 Pc Price (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="border rounded px-2 py-2 w-full"
                                                value={p.rate}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || parseFloat(value) >= 0) {
                                                        const updated = [...brandList];
                                                        updated[bi].packs[pi].rate = value;
                                                        updated[bi].packs[pi].packQty = "1";
                                                        updated[bi].packs[pi].autoRate = value ? Number(value).toFixed(2) : "";
                                                        setBrandList(updated);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                                                }}
                                            />
                                        </div>

                                        <div className="col-span-1">
                                            <label className="text-blue-700 font-medium block mb-1">{rateUnitLabel}</label>
                                            <div className="font-bold text-blue-700 text-center py-2">{p.autoRate || "-"}</div>
                                        </div>

                                        <div className="col-span-1 flex items-end gap-1">
                                            <button onClick={() => addPackRow(bi)} className="bg-blue-600 text-white w-7 h-7 rounded flex items-center justify-center text-sm" title="Add Pack">+</button>
                                            {pi !== 0 && (
                                                <button onClick={() => removePackRow(bi, pi)} className="text-red-500 hover:text-red-700 w-7 h-7 flex items-center justify-center" title="Delete Pack">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid gap-3 items-end grid-cols-4 bg-gray-50 p-2 rounded border border-gray-100">
                                        <div className="col-span-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-gray-700 font-medium">GST</label>
                                                <div className="inline-flex rounded-full bg-gray-100 p-0.5 text-xs font-semibold">
                                                    <button
                                                        type="button"
                                                        onClick={() => updateBrand(bi, pi, "gstType", "exclusive")}
                                                        className={`px-3 py-0.5 rounded-full transition-all ${getPackGstType(p) === "exclusive" ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                                                    >Excl</button>
                                                    <button
                                                        type="button"
                                                        onClick={() => updateBrand(bi, pi, "gstType", "inclusive")}
                                                        className={`px-3 py-0.5 rounded-full transition-all ${getPackGstType(p) === "inclusive" ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                                                    >Incl</button>
                                                </div>
                                            </div>
                                            <select
                                                className="border rounded px-2 py-2 w-full bg-white"
                                                value={getPackGst(p)}
                                                onChange={(e) => updateBrand(bi, pi, "gst", e.target.value)}
                                            >
                                                {GST_OPTIONS.map(g => (<option key={g} value={g}>{g}%</option>))}
                                            </select>
                                        </div>
                                        <div className="col-span-1">
                                            <label className="text-gray-600 font-medium block mb-1 text-xs">Cost / {isLitre ? 'Ltr' : isNos ? 'Pc' : 'Kg'} (excl GST)</label>
                                            <div className="font-bold text-gray-700 text-center py-2 bg-white rounded border border-gray-200">
                                                {(() => {
                                                    const base = Number(p.autoRate);
                                                    if (!base || isNaN(base)) return '-';
                                                    const gst = getPackGst(p);
                                                    const costNoGst = getPackGstType(p) === 'inclusive' ? base / (1 + gst / 100) : base;
                                                    return `₹ ${costNoGst.toFixed(2)}`;
                                                })()}
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <label className="text-amber-700 font-medium block mb-1 text-xs">GST Amount</label>
                                            <div className="font-bold text-amber-700 text-center py-2 bg-white rounded border border-amber-100">
                                                {computeGstAmount(p) !== null ? `₹ ${computeGstAmount(p).toFixed(2)}` : '-'}
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <label className="text-green-700 font-medium block mb-1 text-xs">{rateUnitLabel}</label>
                                            <div className="font-bold text-green-700 text-center py-2 bg-white rounded border border-green-100">
                                                {computePostGstRate(p) !== null ? `₹ ${computePostGstRate(p).toFixed(2)}` : '-'}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="grid gap-3 mb-2 items-end grid-cols-7">

                                        <div className="col-span-2">
                                            <label className="text-gray-700 font-medium block mb-1">Pack Size</label>
                                            <select
                                                className="border rounded px-2 py-2 w-full"
                                                value={p.packQty}
                                                onChange={(e) => updateBrand(bi, pi, "packQty", e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                {(() => {
                                                    const selectedPacks = brand.packs
                                                        .filter((pack, idx) => idx !== pi && pack.packQty && pack.packQty !== "others")
                                                        .map(pack => pack.packQty);
                                                    return getPackOptions(brand.brandType).map(opt => (
                                                        <option
                                                            key={opt.value}
                                                            value={opt.value}
                                                            disabled={selectedPacks.includes(opt.value)}
                                                            style={selectedPacks.includes(opt.value) ? { color: '#ccc' } : {}}
                                                        >
                                                            {opt.label}
                                                        </option>
                                                    ));
                                                })()}
                                            </select>
                                        </div>

                                        <div className="col-span-2">
                                            <label className="text-gray-700 font-medium block mb-1">Rate (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="border rounded px-2 py-2 w-full"
                                                value={p.rate}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || parseFloat(value) >= 0) updateBrand(bi, pi, "rate", value);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
                                                }}
                                            />
                                        </div>

                                        {showGST(p) && (
                                            <div className="col-span-2">
                                                <div className="flex items-center justify-between mb-1">
                                                    <label className="text-gray-700 font-medium">GST</label>
                                                    <div className="inline-flex rounded-full bg-gray-100 p-0.5 text-xs font-semibold">
                                                        <button
                                                            type="button"
                                                            onClick={() => updateBrand(bi, pi, "gstType", "exclusive")}
                                                            className={`px-3 py-0.5 rounded-full transition-all ${getPackGstType(p) === "exclusive" ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                                                        >Excl</button>
                                                        <button
                                                            type="button"
                                                            onClick={() => updateBrand(bi, pi, "gstType", "inclusive")}
                                                            className={`px-3 py-0.5 rounded-full transition-all ${getPackGstType(p) === "inclusive" ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                                                        >Incl</button>
                                                    </div>
                                                </div>
                                                <select
                                                    className="border rounded px-2 py-2 w-full bg-white"
                                                    value={getPackGst(p)}
                                                    onChange={(e) => updateBrand(bi, pi, "gst", e.target.value)}
                                                >
                                                    {GST_OPTIONS.map(g => (<option key={g} value={g}>{g}%</option>))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="col-span-1 flex items-end gap-1">
                                            <button onClick={() => addPackRow(bi)} className="bg-blue-600 text-white w-7 h-7 rounded flex items-center justify-center text-sm" title="Add Pack">+</button>
                                            {pi !== 0 && (
                                                <button onClick={() => removePackRow(bi, pi)} className="text-red-500 hover:text-red-700 w-7 h-7 flex items-center justify-center" title="Delete Pack">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {showGST(p) && (
                                        <div className="grid gap-3 items-end grid-cols-3 bg-gray-50 p-2 rounded border border-gray-100">
                                            <div className="col-span-1">
                                                <label className="text-gray-600 font-medium block mb-1 text-xs">Cost / {isLitre ? 'Ltr' : isNos ? 'Pc' : 'Kg'} (excl GST)</label>
                                                <div className="font-bold text-gray-700 text-center py-2 bg-white rounded border border-gray-200">
                                                    {(() => {
                                                        const base = Number(p.autoRate);
                                                        if (!base || isNaN(base)) return '-';
                                                        const gst = getPackGst(p);
                                                        const costNoGst = getPackGstType(p) === 'inclusive' ? base / (1 + gst / 100) : base;
                                                        return `₹ ${costNoGst.toFixed(2)}`;
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="col-span-1">
                                                <label className="text-amber-700 font-medium block mb-1 text-xs">GST Amount</label>
                                                <div className="font-bold text-amber-700 text-center py-2 bg-white rounded border border-amber-100">
                                                    {computeGstAmount(p) !== null ? `₹ ${computeGstAmount(p).toFixed(2)}` : '-'}
                                                </div>
                                            </div>
                                            <div className="col-span-1">
                                                <label className="text-green-700 font-medium block mb-1 text-xs">{rateUnitLabel}</label>
                                                <div className="font-bold text-green-700 text-center py-2 bg-white rounded border border-green-100">
                                                    {computePostGstRate(p) !== null ? `₹ ${computePostGstRate(p).toFixed(2)}` : '-'}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {p.packQty === "others" && (
                                        <div className="grid grid-cols-6 gap-3 mt-2">
                                            <div className="col-span-2">
                                                <label className="text-gray-700 font-medium block mb-1">Qty</label>
                                                <input
                                                    type="number"
                                                    className="border rounded px-2 py-2 w-full"
                                                    value={p.otherQty || ""}
                                                    onChange={(e) => updateBrand(bi, pi, "otherQty", e.target.value)}
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="text-gray-700 font-medium block mb-1">Unit</label>
                                                <select
                                                    className="border rounded px-2 py-2 w-full"
                                                    value={p.otherUnit || ""}
                                                    onChange={(e) => updateBrand(bi, pi, "otherUnit", e.target.value)}
                                                >
                                                    <option value="">Select</option>
                                                    {isLitre ? (
                                                        <>
                                                            <option value="ml">ml</option>
                                                            <option value="Litre">Litre</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option value="Grams">Grams</option>
                                                            <option value="Kg">Kg</option>
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {p.packQty === "loose" && (
                                        <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 text-sm">
                                            {looseAlertLabel}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            ))}

            <div className="mt-4">
                {saveError && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2">
                        <span className="text-red-600 text-lg leading-none">⛔</span>
                        <div className="text-sm text-red-800 flex-1">
                            <div className="font-semibold mb-0.5">Cannot save</div>
                            <div>{saveError.title}</div>
                            {saveError.lines && saveError.lines.length > 0 && (
                                <ul className="mt-1 list-disc list-inside text-red-700">
                                    {saveError.lines.map((l, i) => <li key={i}>{l}</li>)}
                                </ul>
                            )}
                        </div>
                        <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none">✕</button>
                    </div>
                )}

                {hasHighRate && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
                        <span className="text-amber-600 text-lg">⚠️</span>
                        <div className="text-sm text-amber-800">
                            <strong>Price Alert:</strong> Rate per unit difference is more than 10%, proceed if its correct.
                        </div>
                    </div>
                )}

                <div className="flex justify-between">
                    <button onClick={addBrandRow} className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm">
                        Add Brand
                    </button>
                    <button
                        onClick={() => {
                            setSaveError(null);
                            const brandNames = brandList.map(b => b.brand.trim().toLowerCase());
                            const hasDuplicates = brandNames.some((name, idx) => name && brandNames.indexOf(name) !== idx);
                            if (hasDuplicates) {
                                setSaveError({ title: "Duplicate brand names found. Please use unique brand names.", lines: [] });
                                return;
                            }
                            const emptyBrandType = brandList.some(b => !b.brandType || b.brandType.trim() === "");
                            if (emptyBrandType) {
                                setSaveError({ title: "Brand Type is mandatory for all brands.", lines: [] });
                                return;
                            }

                            // Branded: brand name mandatory, GST must be > 0
                            const brandedMissingName = brandList.some(b => b.brandType === 'Non-JPC' && !b.brand.trim());
                            if (brandedMissingName) {
                                setSaveError({ title: "Brand Name is mandatory for Branded type.", lines: [] });
                                return;
                            }

                            // JPC / Non-JPC validation: rate per kg must not exceed min(State JPC, Regional JPC) * 1.15
                            const jpcRates = [stateJpcRate, jpcRate].filter(r => r !== null && r !== undefined && !isNaN(Number(r))).map(Number);
                            if (jpcRates.length > 0) {
                                const minJpc = Math.min(...jpcRates);
                                const maxAllowed = minJpc * 1.15;
                                const violating = [];
                                brandList.forEach(b => {
                                    if (b.brandType === 'JPC') {
                                        b.packs.forEach(pk => {
                                            const r = Number(pk.autoRate);
                                            if (pk.autoRate && !isNaN(r) && r > maxAllowed) {
                                                violating.push(`${b.brand || '(unnamed)'} — ₹${r.toFixed(2)}`);
                                            }
                                        });
                                    }
                                });
                                if (violating.length > 0) {
                                    setSaveError({
                                        title: `Rate per unit for JPC / Non-JPC brand must not exceed ₹${maxAllowed.toFixed(2)} (min JPC ₹${minJpc} × 1.15).`,
                                        lines: violating,
                                    });
                                    return;
                                }
                            }

                            onSave();
                        }}
                        className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
                    >
                        Save Changes
                    </button>
                </div>
            </div>

            {/* Rate Trend Table */}
            {rateTrend && brandTypes.length > 0 && (
                <div className="mt-6 border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        📊 Rate Trend ({rateUnitLabel})
                    </h4>
                    <p className="text-xs text-gray-500 mb-2">{item.name}</p>

                    {/* State JPC Rate + District JPC Rate — stacked vertically */}
                    <div className="flex flex-col gap-2 mb-3">
                        {stateJpcRate !== null && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 text-xs font-medium text-purple-700">
                                    <span>State JPC Rate:</span>
                                    <span className="font-bold text-purple-800">₹ {stateJpcRate}</span>
                                </div>
                                {stateSuppliers.length > 0 && (
                                    showSuppliers === 'state' ? (
                                        <button
                                            onClick={() => setShowSuppliers(null)}
                                            className="mt-1 text-xs text-purple-600 underline hover:text-purple-800"
                                        >
                                            Hide supplier details
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setShowSuppliers('state')}
                                            className="mt-1 text-xs text-purple-600 underline hover:text-purple-800"
                                        >
                                            Click here to view supplier details
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                        {(jpcRate !== null || suppliers.length > 0) && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 text-xs font-medium text-blue-700">
                                    <span>{districtName} JPC Rate:</span>
                                    {jpcRate !== null
                                        ? <span className="font-bold text-blue-800">₹ {jpcRate}</span>
                                        : <span className="text-blue-400 italic">—</span>
                                    }
                                </div>
                                {suppliers.length > 0 && (
                                    showSuppliers === 'district' ? (
                                        <button
                                            onClick={() => setShowSuppliers(null)}
                                            className="mt-1 text-xs text-blue-600 underline hover:text-blue-800"
                                        >
                                            Hide supplier details
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setShowSuppliers('district')}
                                            className="mt-1 text-xs text-blue-600 underline hover:text-blue-800"
                                        >
                                            Click here to view supplier details
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>

                    {/* Supplier details panel */}
                    {showSuppliers !== null && (
                        (() => {
                            const activeSuppliers = showSuppliers === 'state' ? stateSuppliers : suppliers;
                            const label = showSuppliers === 'state' ? 'State JPC Supplier Details' : `${districtName} JPC Supplier Details`;
                            const colorClass = showSuppliers === 'state'
                                ? { border: 'border-purple-200', header: 'bg-purple-50 border-purple-200 text-purple-700', close: 'text-purple-400 hover:text-purple-600', divider: 'divide-purple-100', row: 'border-purple-100' }
                                : { border: 'border-orange-200', header: 'bg-orange-50 border-orange-200 text-orange-700', close: 'text-orange-400 hover:text-orange-600', divider: 'divide-orange-100', row: 'border-orange-100' };
                            return activeSuppliers.length > 0 ? (
                                <div className={`mb-3 border ${colorClass.border} rounded-lg overflow-hidden`}>
                                    <div className={`${colorClass.header} px-3 py-1.5 flex items-center justify-between border-b`}>
                                        <span className="text-xs font-semibold">{label}</span>
                                        <button onClick={() => setShowSuppliers(null)} className={`${colorClass.close} text-sm`}>✕</button>
                                    </div>
                                    <div className={`divide-y ${colorClass.divider}`}>
                                        {activeSuppliers.map((s, i) => (
                                            <div key={i} className={`px-3 py-2 space-y-2 text-xs border-b ${colorClass.row} last:border-0`}>
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div>
                                                        <p className="text-gray-400">Company</p>
                                                        <p className="font-medium text-gray-800">{s.company || '—'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-400">Contact Person</p>
                                                        <p className="font-medium text-gray-800">{s.contact_person || '—'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-400">Contact Number</p>
                                                        <p className="font-medium text-gray-800">{s.contact_number || '—'}</p>
                                                    </div>
                                                </div>
                                                {s.address && (
                                                    <div>
                                                        <p className="text-gray-400">Address</p>
                                                        <p className="font-medium text-gray-800">{s.address}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null;
                        })()
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200 rounded">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border px-3 py-2 text-left" rowSpan={2}>Brand Type</th>
                                    <th className="border px-3 py-2 text-center" colSpan={2}>{districtName}</th>
                                    <th className="border px-3 py-2 text-center" colSpan={2}>State</th>
                                </tr>
                                <tr className="bg-gray-50">
                                    <th className="border px-3 py-2 text-right text-green-700">Min</th>
                                    <th className="border px-3 py-2 text-right text-red-600">Max</th>
                                    <th className="border px-3 py-2 text-right text-green-700">Min</th>
                                    <th className="border px-3 py-2 text-right text-red-600">Max</th>
                                </tr>
                            </thead>
                            <tbody>
                                {brandTypes.map((type) => (
                                    <tr key={type} className="hover:bg-gray-50">
                                        <td className="border px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded font-medium ${
                                                type === 'JPC' ? 'bg-blue-100 text-blue-700' :
                                                type === 'Cooperative' ? 'bg-green-100 text-green-700' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>{type === 'Non-JPC' ? 'Branded' : type}</span>
                                        </td>
                                        <td className="border px-3 py-2 text-right font-semibold text-green-700">
                                            {districtData[type]?.min != null ? `₹${Number(districtData[type].min).toFixed(2)}` : '-'}
                                        </td>
                                        <td className="border px-3 py-2 text-right font-semibold text-red-600">
                                            {districtData[type]?.max != null ? `₹${Number(districtData[type].max).toFixed(2)}` : '-'}
                                        </td>
                                        <td className="border px-3 py-2 text-right font-semibold text-green-700">
                                            {stateData[type]?.min != null ? `₹${Number(stateData[type].min).toFixed(2)}` : '-'}
                                        </td>
                                        <td className="border px-3 py-2 text-right font-semibold text-red-600">
                                            {stateData[type]?.max != null ? `₹${Number(stateData[type].max).toFixed(2)}` : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

        </div>
    );
};

export default StoreUpdateForm;